/**
 * Files: kind and icon from the MIME type or extension, locale-aware sizes, the inline-preview type policy and the
 * effective permissions on a file (shared with the API, which enforces them), the image grid, dedupe of picked files,
 * the upload state machine, and the outbox rule that a message with attachments is sent only once they're uploaded.
 * Pure: no I/O, no clock, no randomness.
 */
import type { ChannelKind, FileAccessReason, FileAudience, FilePermissions, FileRef, UploadStatus } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";

// ---------- Limits (the API enforces the same numbers) ----------

/** Largest file: 1 GiB. */
export const MAX_FILE_BYTES = 1_073_741_824;
/** Bigger files go through R2 multipart (Workers request bodies are capped at 100 MB). */
export const SINGLE_UPLOAD_MAX_BYTES = 99_614_720;
/** Multipart part size (every part but the last). */
export const UPLOAD_PART_BYTES = 52_428_800;
/** Client-made thumbnails (JPEG/PNG/WebP). */
export const MAX_THUMB_BYTES = 524_288;
/** Longest edge of a client-made thumbnail, px. */
export const THUMB_MAX_EDGE = 800;
export const MAX_FILES_PER_MESSAGE = 10;
/** Unattached uploads are deleted after this long. */
export const ORPHAN_TTL_MS = 86_400_000;

// ---------- Kinds & icons ----------

export type FileKind =
  | "image"
  | "video"
  | "audio"
  | "pdf"
  | "document"
  | "spreadsheet"
  | "presentation"
  | "archive"
  | "code"
  | "text"
  | "executable"
  | "other";

/** Lower-case extension without the dot ("Report.PDF" → "pdf"); "" when there is none (".bashrc" has none). */
export function extensionOf(name: string): string {
  const base = name.slice(name.lastIndexOf("/") + 1);
  const dot = base.lastIndexOf(".");
  if (dot <= 0 || dot === base.length - 1) return "";
  return base.slice(dot + 1).toLowerCase();
}

const EXECUTABLE_EXTENSIONS = [
  "exe", "msi", "msix", "appx", "bat", "cmd", "com", "scr", "pif", "cpl", "ps1", "vbs", "vbe", "wsf", "hta", "reg", "jar",
  "apk", "aab", "ipa", "app", "dmg", "pkg", "deb", "rpm", "sh", "bash", "zsh", "command", "run", "appimage", "dll", "so",
  "dylib", "lnk", "url",
];
const EXECUTABLE_MIMES = [
  "application/x-msdownload", "application/x-msdos-program", "application/x-msi", "application/x-ms-installer",
  "application/vnd.microsoft.portable-executable", "application/x-executable", "application/x-elf",
  "application/x-mach-binary", "application/x-sh", "application/x-csh", "application/x-shellscript",
  "application/x-apple-diskimage", "application/vnd.android.package-archive", "application/java-archive",
  "application/x-java-archive", "application/x-bat", "application/hta", "application/x-ms-shortcut",
];
const BY_EXTENSION: Record<string, FileKind> = {
  png: "image", jpg: "image", jpeg: "image", gif: "image", webp: "image", avif: "image", heic: "image", heif: "image",
  bmp: "image", tif: "image", tiff: "image", svg: "image", ico: "image",
  mp4: "video", m4v: "video", mov: "video", webm: "video", mkv: "video", avi: "video", ogv: "video",
  mp3: "audio", m4a: "audio", aac: "audio", wav: "audio", flac: "audio", ogg: "audio", oga: "audio", opus: "audio",
  pdf: "pdf",
  doc: "document", docx: "document", odt: "document", rtf: "document", pages: "document", epub: "document",
  xls: "spreadsheet", xlsx: "spreadsheet", ods: "spreadsheet", csv: "spreadsheet", tsv: "spreadsheet", numbers: "spreadsheet",
  ppt: "presentation", pptx: "presentation", odp: "presentation", key: "presentation",
  zip: "archive", rar: "archive", "7z": "archive", tar: "archive", gz: "archive", tgz: "archive", bz2: "archive", xz: "archive",
  js: "code", mjs: "code", ts: "code", tsx: "code", jsx: "code", json: "code", html: "code", htm: "code", css: "code",
  xml: "code", py: "code", rb: "code", go: "code", rs: "code", java: "code", kt: "code", swift: "code", c: "code",
  h: "code", cpp: "code", cs: "code", php: "code", sql: "code", yaml: "code", yml: "code", toml: "code",
  txt: "text", md: "text", log: "text",
};
const BY_MIME: Record<string, FileKind> = {
  "application/pdf": "pdf",
  "application/msword": "document",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "document",
  "application/vnd.oasis.opendocument.text": "document",
  "application/rtf": "document",
  "text/rtf": "document",
  "application/epub+zip": "document",
  "application/vnd.apple.pages": "document",
  "application/vnd.ms-excel": "spreadsheet",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "spreadsheet",
  "application/vnd.oasis.opendocument.spreadsheet": "spreadsheet",
  "application/vnd.apple.numbers": "spreadsheet",
  "text/csv": "spreadsheet",
  "text/tab-separated-values": "spreadsheet",
  "application/vnd.ms-powerpoint": "presentation",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "presentation",
  "application/vnd.oasis.opendocument.presentation": "presentation",
  "application/vnd.apple.keynote": "presentation",
  "application/zip": "archive",
  "application/x-zip-compressed": "archive",
  "application/x-7z-compressed": "archive",
  "application/x-rar-compressed": "archive",
  "application/vnd.rar": "archive",
  "application/x-tar": "archive",
  "application/gzip": "archive",
  "application/x-gzip": "archive",
  "application/x-bzip2": "archive",
  "application/x-xz": "archive",
  "application/json": "code",
  "application/xml": "code",
  "application/javascript": "code",
  "application/x-javascript": "code",
  "application/typescript": "code",
  "application/x-yaml": "code",
  "application/sql": "code",
  "application/xhtml+xml": "code",
  "text/html": "code",
  "text/css": "code",
  "text/javascript": "code",
  "text/xml": "code",
  "text/plain": "text",
  "text/markdown": "text",
};

/** MIME type without parameters, lower-cased ("Text/Plain; charset=utf-8" → "text/plain"). */
export function baseMime(mime: string): string {
  const semi = mime.indexOf(";");
  return (semi < 0 ? mime : mime.slice(0, semi)).trim().toLowerCase();
}

/** The usual extension of the types people get as files. Nothing that runs: a sender's type never makes a program. */
const EXTENSION_BY_MIME: Record<string, string> = {
  "application/pdf": "pdf", "text/plain": "txt", "text/csv": "csv", "text/calendar": "ics", "application/ics": "ics",
  "text/markdown": "md", "application/rtf": "rtf", "text/rtf": "rtf", "application/json": "json", "application/xml": "xml", "text/xml": "xml",
  "image/png": "png", "image/jpeg": "jpg", "image/gif": "gif", "image/webp": "webp", "image/avif": "avif", "image/heic": "heic", "image/heif": "heif",
  "image/bmp": "bmp", "image/tiff": "tiff",
  "video/mp4": "mp4", "video/quicktime": "mov", "video/webm": "webm",
  "audio/mpeg": "mp3", "audio/mp4": "m4a", "audio/aac": "aac", "audio/wav": "wav", "audio/flac": "flac", "audio/ogg": "ogg",
  "application/msword": "doc", "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/vnd.oasis.opendocument.text": "odt", "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx", "application/vnd.oasis.opendocument.spreadsheet": "ods",
  "application/vnd.ms-powerpoint": "ppt", "application/vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
  "application/vnd.oasis.opendocument.presentation": "odp", "application/epub+zip": "epub",
  "application/zip": "zip", "application/x-zip-compressed": "zip", "application/gzip": "gz", "application/x-gzip": "gz",
  "application/x-7z-compressed": "7z", "application/vnd.rar": "rar", "application/x-rar-compressed": "rar", "application/x-tar": "tar",
};

/**
 * The name to save a download under: an extension from the content type when the name has none (a file without one
 * opens in nothing), and "attachment" when there's no name at all. A name with an extension stays as it is.
 */
export function filenameWithExtension(filename: string, mime: string): string {
  const name = filename.trim() === "" ? "attachment" : filename;
  if (/[^.]\.[^./\\\s]+$/.test(name)) return name;
  const ext = EXTENSION_BY_MIME[baseMime(mime)];
  return ext === undefined ? name : `${name.replace(/\.+$/, "")}.${ext}`;
}

/** Images a browser shows without running anything (no SVG), by type and by extension. */
const PASSIVE_IMAGES: Record<string, string> = {
  png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp", avif: "image/avif", bmp: "image/bmp",
};
/** Text a browser shows as text: forced to text/plain, since an XML or HTML blob would run in the app's origin. */
const PLAIN_TEXT_MIMES = ["text/plain", "text/csv", "text/xml", "application/xml", "application/json", "text/markdown", "text/x-log"];
const PLAIN_TEXT_EXTENSIONS = ["txt", "csv", "xml", "json", "md", "log"];

/**
 * What a mail attachment opens as in a browser tab: the type its blob gets, or undefined when it only downloads (like
 * Gmail: PDFs, pictures and text preview, the rest is a download). PDFs and passive images keep their type; text, XML,
 * JSON and CSV show as plain text. SVG, HTML and anything that runs never open.
 */
export function openInTabAs(mime: string, name: string): string | undefined {
  const kind = fileKind(mime, name);
  if (kind === "executable") return undefined;
  const m = baseMime(mime);
  const ext = extensionOf(name);
  const generic = m === "" || m === "application/octet-stream" || m === "binary/octet-stream";
  if (kind === "pdf") return "application/pdf";
  if (Object.values(PASSIVE_IMAGES).includes(m)) return m;
  if (generic && PASSIVE_IMAGES[ext] !== undefined) return PASSIVE_IMAGES[ext];
  if (PLAIN_TEXT_MIMES.includes(m) || ((generic || m.startsWith("text/")) && PLAIN_TEXT_EXTENSIONS.includes(ext))) {
    return "text/plain;charset=utf-8";
  }
  return undefined;
}

/**
 * What kind of file this is, from its MIME type, falling back to the extension when the type is missing or generic.
 * Executables are recognised by either (a "photo.exe" sent as image/png is still an executable).
 */
export function fileKind(mime: string, name: string): FileKind {
  const m = baseMime(mime);
  const ext = extensionOf(name);
  if (EXECUTABLE_EXTENSIONS.includes(ext) || EXECUTABLE_MIMES.includes(m)) return "executable";
  if (m !== "" && m !== "application/octet-stream" && m !== "binary/octet-stream") {
    const known = BY_MIME[m];
    if (known !== undefined) return known;
    if (m.startsWith("image/")) return "image";
    if (m.startsWith("video/")) return "video";
    if (m.startsWith("audio/")) return "audio";
    if (m.startsWith("text/x-")) return "code";
    if (m.startsWith("text/")) return "text";
  }
  return BY_EXTENSION[ext] ?? "other";
}

/** Icon (key in packages/tokens/icons.json) for a file kind. */
export function fileIcon(kind: FileKind): string {
  switch (kind) {
    case "image":
      return "fileImage";
    case "video":
      return "fileVideo";
    case "audio":
      return "fileAudio";
    case "pdf":
    case "document":
    case "text":
      return "fileText";
    case "spreadsheet":
      return "fileSpreadsheet";
    case "presentation":
      return "filePresentation";
    case "archive":
      return "fileArchive";
    case "code":
      return "fileCode";
    case "executable":
      return "fileExecutable";
    case "other":
      return "file";
  }
}

/** Translatable name of a kind ("Spreadsheet"). */
export function kindLabel(kind: FileKind): MessageDescriptor {
  return msg(`files.kind.${kind}`);
}

/** MIME types served inline (rendered by the browser / native viewers). Everything else is download-only. */
const INLINE_MIMES = [
  "image/png", "image/jpeg", "image/gif", "image/webp", "image/avif", "image/bmp", "image/heic", "image/heif",
  "video/mp4", "video/webm", "video/quicktime", "video/ogg",
  "audio/mpeg", "audio/mp4", "audio/aac", "audio/ogg", "audio/wav", "audio/x-wav", "audio/webm", "audio/flac",
  "application/pdf", "text/plain",
];

/**
 * The type policy: may this file be rendered inline? Only an allowlist of passive media types, never executables
 * (by type or extension). SVG, HTML, XML and scripts can run code when opened, so they are download-only.
 */
export function inlinePreview(mime: string, name: string): boolean {
  return fileKind(mime, name) !== "executable" && INLINE_MIMES.includes(baseMime(mime));
}

/**
 * How a file renders in a message: in the image grid, as a video player, or as a card (icon, name, size).
 * Images show for everyone who can see the message (a non-inline image, e.g. SVG, only through its thumbnail);
 * videos stream the original, so they need `canDownload`.
 */
export function fileDisplay(file: FileRef, canDownload: boolean): "image" | "video" | "card" {
  const kind = fileKind(file.mime, file.name);
  if (!inlinePreview(file.mime, file.name)) return kind === "image" && file.thumbUrl !== undefined ? "image" : "card";
  if (kind === "image") return "image";
  if (kind === "video" && canDownload) return "video";
  return "card";
}

/** The source a viewer shows for an image: the original when it may render inline, else its thumbnail. */
export function imageSource(file: FileRef): string {
  return inlinePreview(file.mime, file.name) || file.thumbUrl === undefined ? file.url : file.thumbUrl;
}

// ---------- Sizes ----------

const UNITS = ["B", "KB", "MB", "GB", "TB"];

/**
 * Human file size, 1024-based, one decimal below 10 and none above, with the locale's decimal separator:
 * 0 → "0 B", 1536 → "1.5 KB" (pt-BR "1,5 KB"), 10_485_760 → "10 MB", 1_073_741_824 → "1 GB".
 */
export function formatFileSize(bytes: number, locale: string): string {
  const n = Math.max(0, Math.floor(bytes));
  if (n < 1024) return `${n} B`;
  let unit = 0;
  let value = n;
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  let tenths = Math.round(value * 10);
  if (tenths >= 100) tenths = Math.round(value) * 10;
  if (tenths >= 10240 && unit < UNITS.length - 1) {
    unit += 1;
    tenths = 10;
  }
  const whole = Math.floor(tenths / 10);
  const frac = tenths % 10;
  const sep = locale.toLowerCase().startsWith("pt") ? "," : ".";
  const num = whole >= 10 || frac === 0 ? String(whole) : `${whole}${sep}${frac}`;
  return `${num} ${UNITS[unit]!}`;
}

// ---------- Image grid ----------

export type GridCell = { index: number; width: number; height: number; /** "+N" overlay on the last cell. */ more: number };
export type GridRow = { height: number; cells: GridCell[] };
export type ImageGrid = { width: number; height: number; gap: number; rows: GridRow[] };

export const GRID_GAP = 4;
const GRID_MAX_WIDTH = 400;
const SINGLE_MAX_HEIGHT = 300;
const SINGLE_MIN_EDGE = 48;

/**
 * Layout for the images of one message, in px, row by row: 1 image keeps its aspect ratio (fit in 400×300, or
 * `maxWidth` when narrower); 2 → two squares; 3 → one wide on top + two below; 4 or more → 2×2 with "+N" on the
 * fourth. Unknown sizes count as 4:3.
 */
export function imageGrid(images: { width?: number; height?: number }[], maxWidth: number): ImageGrid {
  const W = Math.max(SINGLE_MIN_EDGE * 2 + GRID_GAP, Math.min(Math.floor(maxWidth), GRID_MAX_WIDTH));
  const n = images.length;
  if (n === 0) return { width: 0, height: 0, gap: GRID_GAP, rows: [] };
  if (n === 1) {
    const img = images[0]!;
    const w0 = img.width !== undefined && img.width > 0 ? img.width : 4;
    const h0 = img.height !== undefined && img.height > 0 ? img.height : 3;
    const known = img.width !== undefined && img.width > 0 && img.height !== undefined && img.height > 0;
    const scale = Math.min(W / w0, SINGLE_MAX_HEIGHT / h0, known ? 1 : Number.POSITIVE_INFINITY);
    const width = Math.max(SINGLE_MIN_EDGE, Math.round(w0 * scale));
    const height = Math.max(SINGLE_MIN_EDGE, Math.round(h0 * scale));
    return { width, height, gap: GRID_GAP, rows: [{ height, cells: [{ index: 0, width, height, more: 0 }] }] };
  }
  const left = Math.floor((W - GRID_GAP) / 2);
  const right = W - GRID_GAP - left;
  const pair = (a: number, h: number, more: number): GridRow => ({
    height: h,
    cells: [
      { index: a, width: left, height: h, more: 0 },
      { index: a + 1, width: right, height: h, more },
    ],
  });
  let rows: GridRow[];
  if (n === 2) rows = [pair(0, left, 0)];
  else if (n === 3) {
    const top = Math.floor(W / 2);
    rows = [{ height: top, cells: [{ index: 0, width: W, height: top, more: 0 }] }, pair(1, Math.floor((left * 3) / 4), 0)];
  } else {
    const h = Math.floor((left * 3) / 4);
    rows = [pair(0, h, 0), pair(2, h, n - 4)];
  }
  const height = rows.reduce((sum, r) => sum + r.height, 0) + GRID_GAP * (rows.length - 1);
  return { width: W, height, gap: GRID_GAP, rows };
}

/** Scales a media size down (never up) to fit `maxWidth`×`maxHeight`; unknown sizes become 4:3 at full width. */
export function fitWithin(width: number | undefined, height: number | undefined, maxWidth: number, maxHeight: number): { width: number; height: number } {
  if (width === undefined || height === undefined || width <= 0 || height <= 0) {
    const w = Math.min(maxWidth, Math.floor((maxHeight * 4) / 3));
    return { width: w, height: Math.floor((w * 3) / 4) };
  }
  const scale = Math.min(1, maxWidth / width, maxHeight / height);
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

// ---------- Picking ----------

/** Identity of a picked file for dedupe (the same file dropped or pasted twice). */
export type PickedKey = { name: string; size: number; lastModified: number };

export function pickedKey(f: PickedKey): string {
  return `${f.name}\u0000${f.size}\u0000${f.lastModified}`;
}

/** Indices of `incoming` to keep: not already in `existing` and not repeated earlier in `incoming`. */
export function dedupeFiles(existing: PickedKey[], incoming: PickedKey[]): number[] {
  const seen = new Set(existing.map(pickedKey));
  const keep: number[] = [];
  incoming.forEach((f, i) => {
    const k = pickedKey(f);
    if (seen.has(k)) return;
    seen.add(k);
    keep.push(i);
  });
  return keep;
}

/** Why a picked file can't be attached (null = fine). `count` = files already attached to the draft. */
export function attachProblem(file: { name: string; size: number }, count: number): MessageDescriptor | null {
  if (count >= MAX_FILES_PER_MESSAGE) return msg("files.attach.tooMany", { max: MAX_FILES_PER_MESSAGE });
  if (file.size > MAX_FILE_BYTES) return msg("files.attach.tooBig", { name: file.name });
  return null;
}

// ---------- Upload state machine ----------

/** queued → uploading(progress) → done | failed (retry → queued). */
export type UploadPhase = "queued" | "uploading" | "done" | "failed";

export type UploadState = {
  phase: UploadPhase;
  /** Bytes the server has (or that are in flight in the current request). */
  sent: number;
  total: number;
  /** Upload attempts started so far. */
  attempts: number;
  /** done: the stored file. */
  file?: FileRef;
  /** failed: the ApiErrorKind of the failure. */
  error?: string;
  /** failed: retrying can help (network / server / rate limit). */
  retryable?: boolean;
};

export type UploadEvent =
  | { type: "start" }
  | { type: "progress"; sent: number }
  | { type: "succeed"; file: FileRef }
  | { type: "fail"; error: string }
  /** The network went away mid-upload: back to the queue (not a failure, attempts are kept). */
  | { type: "pause" }
  | { type: "retry" };

export const MAX_AUTO_RETRIES = 5;

export function newUpload(total: number): UploadState {
  return { phase: "queued", sent: 0, total: Math.max(0, total), attempts: 0 };
}

function isRetryable(error: string): boolean {
  return error === "network" || error === "server" || error === "rateLimited";
}

/** Next state; events that don't apply in the current phase leave it unchanged. */
export function uploadReducer(s: UploadState, e: UploadEvent): UploadState {
  switch (e.type) {
    case "start":
      return s.phase === "queued" ? { phase: "uploading", sent: s.sent, total: s.total, attempts: s.attempts + 1 } : s;
    case "progress":
      return s.phase === "uploading" ? { ...s, sent: Math.min(Math.max(e.sent, 0), s.total) } : s;
    case "succeed":
      return s.phase === "uploading" || s.phase === "queued" ? { phase: "done", sent: s.total, total: s.total, attempts: s.attempts, file: e.file } : s;
    case "fail":
      return s.phase === "uploading" ? { phase: "failed", sent: s.sent, total: s.total, attempts: s.attempts, error: e.error, retryable: isRetryable(e.error) } : s;
    case "pause":
      return s.phase === "uploading" ? { phase: "queued", sent: s.sent, total: s.total, attempts: s.attempts } : s;
    case "retry":
      return s.phase === "failed" ? { phase: "queued", sent: s.sent, total: s.total, attempts: s.attempts } : s;
  }
}

/** 0…1 (done = 1; an empty file is done at once). */
export function uploadProgress(s: UploadState): number {
  if (s.phase === "done") return 1;
  if (s.total <= 0) return 0;
  return Math.min(1, s.sent / s.total);
}

/** A failed upload the platform retries on its own (with `uploadRetryDelay`). */
export function shouldAutoRetry(s: UploadState): boolean {
  return s.phase === "failed" && s.retryable === true && s.attempts < MAX_AUTO_RETRIES;
}

/** Backoff before automatic retry n (1-based attempts so far): 1 s, 2 s, 4 s … capped at 30 s. */
export function uploadRetryDelay(attempts: number): number {
  return Math.min(30_000, 1000 * 2 ** Math.max(0, attempts - 1));
}

/** Chip caption for an upload. */
export function uploadLabel(s: UploadState): MessageDescriptor {
  switch (s.phase) {
    case "queued":
      return msg("files.upload.queued");
    case "uploading":
      return msg("files.upload.progress", { percent: Math.floor(uploadProgress(s) * 100) });
    case "done":
      return msg("files.upload.done");
    case "failed":
      return msg("files.upload.failed");
  }
}

// ---------- Outbox: messages wait for their uploads ----------

export type AttachmentsStatus = { status: "waiting" } | { status: "ready"; fileIds: string[] } | { status: "failed" };

/**
 * Whether a queued message with attachments can be sent: "ready" (with the server file ids, in order) once every
 * upload is done; "failed" when one is missing or failed for good; otherwise "waiting" (uploading, queued offline,
 * or about to be retried automatically).
 */
export function attachmentsStatus(uploadIds: string[], uploads: Record<string, UploadState>): AttachmentsStatus {
  const fileIds: string[] = [];
  let waiting = false;
  for (const id of uploadIds) {
    const u = uploads[id];
    if (u === undefined) return { status: "failed" };
    if (u.phase === "failed" && !shouldAutoRetry(u)) return { status: "failed" };
    if (u.phase === "done" && u.file !== undefined) fileIds.push(u.file.id);
    else waiting = true;
  }
  return waiting ? { status: "waiting" } : { status: "ready", fileIds };
}

/** Combined progress of a message's uploads, weighted by size (0…1). */
export function attachmentsProgress(uploadIds: string[], uploads: Record<string, UploadState>): number {
  let sent = 0;
  let total = 0;
  for (const id of uploadIds) {
    const u = uploads[id];
    if (u === undefined) continue;
    total += Math.max(u.total, 1);
    sent += uploadProgress(u) * Math.max(u.total, 1);
  }
  return total === 0 ? 0 : Math.min(1, sent / total);
}

// ---------- Permissions (file page; enforced by the API with this same function) ----------

export type FileAccessInput = {
  status: UploadStatus;
  /** The caller uploaded it. */
  isUploader: boolean;
  /** Where it lives: a conversation, a page, the whole workspace (sticker images), or nowhere yet. */
  scope: "channel" | "page" | "workspace" | "none";
  /** scope "channel". */
  channelKind?: ChannelKind;
  /** The caller is in that conversation. */
  isMember: boolean;
  /** Restricted caller (guest or agent). */
  restricted: boolean;
  /** Restricted caller the channel was shared with (they may join it). */
  granted: boolean;
  /** scope "page": the caller can see the page. */
  pageVisible: boolean;
  /** The caller's `can.downloadFiles`. */
  downloadFiles: boolean;
  /** The caller's access has expired. */
  expired: boolean;
  mime: string;
  name: string;
};

const NONE: FilePermissions = { canView: false, canPreview: false, canDownload: false, canJoin: false, reasons: [] };

/**
 * Effective permissions on a file:
 * - unshared or pending uploads: only the uploader;
 * - channel files: members, plus every unrestricted member for public channels (who may also join); restricted members
 *   only in their own channels (a channel shared with them can be joined);
 * - page files: whoever can see the page;
 * - workspace files (sticker images): every member;
 * - restricted members without `can.downloadFiles` view images inline but never download originals;
 * - the type policy (`inlinePreview`) decides what may render inline at all.
 */
export function fileAccess(a: FileAccessInput): FilePermissions {
  if (a.expired) return { ...NONE, reasons: ["access_expired"] };
  const reasons: FileAccessReason[] = [];
  let canView: boolean;
  let canJoin = false;
  if (a.status === "pending" || a.scope === "none") {
    canView = a.isUploader;
    if (a.status === "pending") reasons.push("pending");
  } else if (a.scope === "page") {
    canView = a.pageVisible;
  } else if (a.scope === "workspace") {
    // Sticker images: workspace content every member may see (and send), like custom emoji.
    canView = true;
  } else {
    const isPublic = a.channelKind === "public";
    canView = a.isMember || (isPublic && !a.restricted);
    if (!a.isMember) canJoin = a.restricted ? a.granted && a.channelKind !== "dm" && a.channelKind !== "group_dm" : isPublic;
    if (!canView) reasons.push("not_in_channel");
  }
  if (!canView) return { ...NONE, canJoin, reasons };
  const ready = a.status === "ready";
  const mayDownload = !a.restricted || a.downloadFiles || a.isUploader;
  if (!mayDownload) reasons.push("no_download_permission");
  const typeInline = inlinePreview(a.mime, a.name);
  if (!typeInline) reasons.push("preview_blocked");
  const kind = fileKind(a.mime, a.name);
  return {
    canView: true,
    canPreview: ready && typeInline && (mayDownload || kind === "image"),
    canDownload: ready && mayDownload,
    canJoin,
    reasons,
  };
}

/** Why Download is off, for the file page and the viewer (null when it's allowed). */
export function downloadBlockedReason(p: FilePermissions, channelName: string | undefined, isAgent: boolean): MessageDescriptor | null {
  if (p.canDownload) return null;
  if (p.reasons.includes("access_expired")) return msg("files.reason.expired");
  if (p.reasons.includes("pending")) return msg("files.reason.pending");
  if (p.reasons.includes("not_in_channel")) {
    if (channelName === undefined || channelName === "") return msg("files.reason.notInConversation");
    return msg(p.canJoin ? "files.reason.notInChannelJoin" : "files.reason.notInChannel", { channel: channelName });
  }
  if (p.reasons.includes("no_download_permission")) return msg(isAgent ? "files.reason.agentNoDownload" : "files.reason.guestNoDownload");
  return null;
}

/** The "Who can access" lines of the file page. `names`: DM participants' display names, already joined. */
export function audienceLines(a: FileAudience, names: string): MessageDescriptor[] {
  const lines: MessageDescriptor[] = [];
  const count = a.memberCount ?? 0;
  switch (a.kind) {
    case "channel":
      lines.push(msg(a.channelKind === "public" ? "files.audience.publicChannel" : "files.audience.privateChannel", { channel: a.channelName ?? "" }));
      lines.push(msg("files.audience.memberCount", { count }));
      break;
    case "dm":
      lines.push(msg("files.audience.dm", { names }));
      break;
    case "page":
      lines.push(msg("files.audience.page", { title: a.pageTitle ?? "" }));
      break;
    case "private":
      lines.push(msg("files.audience.onlyYou"));
      break;
    case "workspace":
      lines.push(msg("files.audience.workspace"));
      break;
  }
  const guests = a.guestCount ?? 0;
  const without = Math.min(guests, a.guestsWithoutDownload ?? 0);
  if (guests > 0) {
    if (without === 0) lines.push(msg("files.audience.guests", { count: guests }));
    else if (without === guests) lines.push(msg("files.audience.guestsNoDownload", { count: guests }));
    else lines.push(msg("files.audience.guestsMixed", { count: guests, without }));
  }
  return lines;
}
